import React from 'react'

const OccasionTypeSelector = ({ value, onChange, error }) => {
  const occasionTypes = [
    {
      value: 'none',
      label: 'No Schedule',
      description: 'Template is always available',
      icon: '🔄',
      disabled: true // This option is handled by the main toggle
    },
    {
      value: 'specific_date',
      label: 'Specific Date',
      description: 'Available on a single specific date',
      icon: '📅',
      example: 'New Year\'s Day, Product Launch'
    },
    {
      value: 'date_range',
      label: 'Date Range',
      description: 'Available during a specific period',
      icon: '📊',
      example: 'Summer season, Holiday period'
    },
    {
      value: 'recurring_yearly',
      label: 'Recurring Yearly',
      description: 'Same date range every year',
      icon: '🔁',
      example: 'Christmas, Birthday, Anniversary'
    },
    {
      value: 'weekly',
      label: 'Weekly Pattern',
      description: 'Specific days of the week',
      icon: '📆',
      example: 'Every Monday, Weekends only'
    },
    {
      value: 'monthly',
      label: 'Monthly Pattern',
      description: 'Specific days or patterns each month',
      icon: '🗓️',
      example: '1st & 15th, First Friday, Last Monday'
    }
  ]

  const activeTypes = occasionTypes.filter(type => !type.disabled)

  return (
    <div className="occasion-type-selector">
      <label className="block text-sm font-medium text-gray-700 mb-3">
        Occasion Type
      </label>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {activeTypes.map((type) => (
          <div
            key={type.value}
            className={`relative cursor-pointer rounded-lg border-2 p-4 transition-all duration-200 hover:shadow-md ${
              value === type.value
                ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                : 'border-gray-200 bg-white hover:border-gray-300'
            }`}
            onClick={() => onChange(type.value)}
          >
            {/* Radio button */}
            <div className="flex items-start">
              <div className="flex items-center h-5">
                <input
                  type="radio"
                  name="occasionType"
                  value={type.value}
                  checked={value === type.value}
                  onChange={() => onChange(type.value)}
                  className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                />
              </div>
              
              <div className="ml-3 flex-1">
                {/* Icon and Label */}
                <div className="flex items-center mb-2">
                  <span className="text-2xl mr-2">{type.icon}</span>
                  <label className="text-sm font-medium text-gray-900 cursor-pointer">
                    {type.label}
                  </label>
                </div>
                
                {/* Description */}
                <p className="text-sm text-gray-600 mb-2">
                  {type.description}
                </p>
                
                {/* Example */}
                {type.example && (
                  <p className="text-xs text-gray-500 italic">
                    Example: {type.example}
                  </p>
                )}
              </div>
            </div>
            
            {/* Selected indicator */}
            {value === type.value && (
              <div className="absolute top-2 right-2">
                <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      
      {/* Error message */}
      {error && (
        <p className="text-red-600 text-sm mt-2">{error}</p>
      )}
      
      {/* Help text based on selected type */}
      {value && value !== 'none' && (
        <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <div className="flex items-start">
            <div className="text-blue-500 mr-2">💡</div>
            <div className="text-sm text-blue-800">
              {value === 'specific_date' && (
                <div>
                  <strong>Specific Date:</strong> Choose a single date when this template should be available. 
                  Perfect for one-time events, product launches, or special announcements.
                </div>
              )}
              
              {value === 'date_range' && (
                <div>
                  <strong>Date Range:</strong> Set a start and end date for when this template is available. 
                  Great for seasonal campaigns, limited-time offers, or event periods.
                </div>
              )}
              
              {value === 'recurring_yearly' && (
                <div>
                  <strong>Recurring Yearly:</strong> The same date range will repeat every year. 
                  Perfect for holidays, anniversaries, or annual events like Christmas or Black Friday.
                </div>
              )}
              
              {value === 'weekly' && (
                <div>
                  <strong>Weekly Pattern:</strong> Choose specific days of the week when this template is available. 
                  Great for weekly newsletters, Monday deals, or weekend specials.
                </div>
              )}
              
              {value === 'monthly' && (
                <div>
                  <strong>Monthly Pattern:</strong> Set specific days or patterns each month. 
                  You can choose exact dates (1st, 15th) or patterns like "first Friday" or "last Monday".
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export { OccasionTypeSelector }
